import { useRef, type ReactNode } from 'react'
import type { AgentRow } from '../transport/api'
import { Icon } from '../ui/Icon'
import { PixelPet } from '../identity/PixelPet'
import { SidebarSection as Section } from './SidebarSection'
import { usePinnedDrag } from './usePinnedDrag'
import { FIRST_TAG, pinKey, targetOfPin, type Pin, type PinTag, type PinTarget } from './usePins'

/** A pin and, when the roster has it, the agent it names. */
export type PinnedRow = { p: Pin; a: AgentRow | undefined }

/**
 * Where a tag's section keeps its open/closed state. The first tag keeps the key the
 * one Pinned section always had, so a rail that had it folded finds it folded.
 */
export const pinSectionId = (tagId: string): string => (tagId === FIRST_TAG ? 'pinned' : `pinned:${tagId}`)

/**
 * ONE TAG'S PINNED AGENTS — the section the rail used to have one of.
 *
 * Owner, 2026-10-12: 這樣子在釘在最上面的就可以有各種不同的 Pinned 的標籤 … 一群 Agent
 * 專門用來做網頁, 一群 Agent 做公司的 project. Each tag with something under it is one
 * of these, in the order the tags stand; an empty one is not drawn (the rule the
 * single section already had: no heading over nothing).
 *
 * A component rather than a block inside Sidebar because the vertical drag is a hook
 * with its own measured geometry, and there are now as many of them as there are
 * tags. A drag reorders WITHIN its tag — moving an agent to another tag is what the
 * pin on its row is for.
 *
 * A live row where the roster has the agent, and a greyed one where it does not: a
 * pinned agent that is not in the roster is still a row, because silently dropping it
 * would make "the machine is off" look like "you never pinned this".
 */
export function PinnedSection({
  tag,
  rows,
  isOpen,
  onToggle,
  agentRow,
  whyGone,
  onMove,
  onPin,
  onEdit,
}: {
  tag: PinTag
  /** What is drawn — already narrowed by the rail's search. */
  rows: PinnedRow[]
  isOpen: boolean
  onToggle: (id: string) => void
  /** The rail's one way to draw an agent row, with something leading it. */
  agentRow: (a: AgentRow, lead?: ReactNode) => ReactNode
  /** Why a pinned agent has no live row. */
  whyGone: (p: Pin) => string
  /** A row was dragged from one place among `rows` to another. */
  onMove: (from: number, to: number) => void
  /** The pin on a greyed row was pressed (a live row's pin is `agentRow`'s own). */
  onPin: (t: PinTarget) => void
  /** The heading's pencil: rename or remove this tag. */
  onEdit: () => void
}) {
  const dragRef = useRef<HTMLDivElement>(null)
  const drag = usePinnedDrag(rows.length, dragRef, onMove)
  /** The wrapper each pinned row rides in: measured by the drag, shifted while
   *  a sibling is lifted, and the surface the landing preview plays on. */
  const wrapStyle = (i: number): React.CSSProperties | undefined => {
    if (drag.from === null || drag.to === null) return undefined
    if (i === drag.from) return { transform: `translateY(${drag.delta}px)` }
    if (drag.from < i && i <= drag.to) return { transform: `translateY(-${drag.rowH}px)` }
    if (drag.to <= i && i < drag.from) return { transform: `translateY(${drag.rowH}px)` }
    return undefined
  }
  const handle = (i: number, session: string) => (
    <span
      className="sb-draghandle"
      title={`Drag to reorder ${session}`}
      aria-label={`Drag to reorder ${session}`}
      {...drag.handleProps(i)}
    >
      <Icon name="drag_indicator" size={14} />
    </span>
  )

  return (
    <Section
      id={pinSectionId(tag.id)}
      label={tag.name}
      kind="tag"
      count={rows.length}
      isOpen={isOpen}
      onToggle={onToggle}
      actions={
        <button
          type="button"
          className="sb-sec-act"
          title={`Rename or remove the tag ${tag.name}`}
          aria-label={`Edit the tag ${tag.name}`}
          onClick={onEdit}
        >
          <Icon name="edit" size={14} />
        </button>
      }
    >
      <div ref={dragRef}>
        {rows.map(({ p, a }, i) => (
          <div
            key={pinKey(p.device, p.hash)}
            data-pin-row
            className={`${i === drag.from ? 'sb-dragging' : drag.from !== null ? 'sb-drag-shift' : ''}`}
            style={wrapStyle(i)}
          >
            {a ? (
              agentRow(a, handle(i, a.session))
            ) : (
              <div
                className="sb-row sb-pin-gone"
                role="treeitem"
                aria-selected={false}
                aria-disabled={true}
                title={`${p.session} — ${whyGone(p)}`}
              >
                {handle(i, p.session)}
                <span className="sb-pet">
                  <PixelPet name={p.session} seed={p.hash} size={26} />
                </span>
                <span className="sb-meta">
                  <span className="sb-name">{p.session}</span>
                  <span className="sb-sub">{whyGone(p)}</span>
                </span>
                <button
                  type="button"
                  className="sb-pin on"
                  title={`${p.session} is pinned — move it to another tag, or unpin`}
                  aria-label={`Change the pin on ${p.session}`}
                  aria-pressed={true}
                  aria-haspopup="dialog"
                  onClick={(e) => {
                    e.stopPropagation()
                    onPin(targetOfPin(p))
                  }}
                >
                  <Icon name="push_pin" size={14} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Section>
  )
}
